import BigNumber from 'bignumber.js'
import FormValidator from 'config/constants/formValidator'
import { ChainIdEnum } from 'config/constants/network'
import { BIG_ZERO } from 'config/constants/number'
import { Token } from 'config/types'
import { ValidationError } from 'config/types/validator'
import useForm from 'hooks/useForm'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import { useIsDisabledFeaturedOnTelegram } from 'hooks/useTelegram'
import Column from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import ProfileService from 'services/ProfileService'
import { useAppSelector } from 'state'
import { useTokenUsdPrice } from 'state/app/hooks'
import { useUserInfo } from 'state/auth/hooks'
import { useProfileBaseTier, useTokenAppBalance } from 'state/profile/hooks'
import { useTokenSelected } from 'state/session/hooks'
import styled from 'styled-components'
import FormInput from 'UIKit/FormControl/FormInput'
import InputAdornment from 'UIKit/Input/InputAdornment'
import RemoveValueInputAdornment from 'UIKit/Input/InputAdornment/RemoveValueInputAdornment'
import { InputContainer, StyledInput } from 'UIKit/Input/styled'
import Text from 'UIKit/Text'
import { getBalanceTokenKey } from 'utils'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { isFiatNetwork } from 'utils/network'
import { transformHunnyRequest } from 'utils/requestHelper'
import WarningOnTelegram from 'views/Authentication/components/Wallet/WarningOnTelegram/WarningOnTelegram'
import DepositRolloverOverview from 'views/Payment/components/DepositRollover/DepositRolloverOverview'
import ExtraInfo from 'views/Payment/components/ExtraInfo'
import { useDepositRolloverOverview } from 'views/Payment/components/Withdraw/WithdrawCryptos/hooks'
import CurrencySelectDropdown from 'views/Payment/DesktopPayment/components/CurrencySelectDropdown'
import { PaymentPageEnums } from 'views/Payment/Menu'
import {
  AmountDisplay,
  LockBonusAmountWithdrawOverview,
  PaymentFormControl,
  PaymentFormInputAmount,
  PaymentLabel,
  PaymentStroke,
  StickyButtonWrapper,
  StyledActionButton,
} from 'views/Payment/MobilePayment/components/styles'
import PaymentTokenSelectModal from '../../components/PaymentTokenSelectModal'
import SelectedCurrencyItem from '../../components/SelectedCurrencyItem'
import { useAvailableDepositTokens } from '../../hooks/useTokens'
import { useLockAmountTipInfo } from '../hooks'
import ConfirmTipModal from './ConfirmTipModal'
import TipAcceptModal from './TipAcceptModal'

const MAX_MESSAGE_CHARACTERS = 200
const MINIMUM_TIP_AMOUNT = 0.1
const TIP_LEVEL_CONDITION = 4

const TipForm: React.FC<{
  initialToken?: Token
  userName: string
  onDismiss: () => void
}> = ({ initialToken, userName, onDismiss }) => {
  const [handlePresentTipConfirmModal] = useModal(ConfirmTipModal)
  const [onPresentSelectTokenModal] = useModal(PaymentTokenSelectModal)

  const { isMobile } = useMatchBreakpoints()
  const isDisabledFeaturedOnTelegram = useIsDisabledFeaturedOnTelegram()

  const userBaseTier = useProfileBaseTier()
  const isAcceptTip = userBaseTier?.level >= TIP_LEVEL_CONDITION
  const playToken = useTokenSelected()
  const { t } = useTranslation()
  const { displayName, avatar } = useUserInfo()
  const [selectedToken, setSelectedToken] = useState<Token>(initialToken || playToken)
  const selectedBalance = useTokenAppBalance(selectedToken)
  const { lockAmount, depositRolloverList } = useLockAmountTipInfo(selectedToken)
  const { totalDepositRollover, openDepositRollovermodal } = useDepositRolloverOverview(depositRolloverList)

  const availableSendTipAmount = useMemo(() => {
    const availableAmount = selectedBalance?.amount?.minus(lockAmount?.lockBonusAmount || 0).minus(totalDepositRollover)
    return availableAmount.gt(0) ? availableAmount : BIG_ZERO
  }, [selectedBalance, lockAmount, totalDepositRollover])

  const selectedTokenUsdAmount = useTokenUsdPrice(selectedToken)
  const minimumSelectedTokenUsdAmount = new BigNumber(MINIMUM_TIP_AMOUNT).dividedBy(selectedTokenUsdAmount)
  const payloadUserRef = useRef(null)
  const { currencies } = useAvailableDepositTokens({
    onCurrencySelect: setSelectedToken,
    selectedCurrency: selectedToken,
    hideHUSDToken: true,
    isIncludeFiat: true,
  })
  const tokenBalances = useAppSelector((state) => state.profile.balances)

  const buildUsernameExistedFn = (value: string) => {
    const request = ProfileService.findUserData(value)
    return transformHunnyRequest(request, (res) => {
      if (res?.data) {
        payloadUserRef.current = {
          avatar: res.data.avatar,
          uid: res.data.uid,
          levelId: res.data.levelId,
        }
      }

      return res?.code === 'error_data_not_found'
    })
  }

  const handleCopySampleMessage = (message: string) => {
    const translatedByContextMessage = t([message])
    controls.message.onValueChanged(translatedByContextMessage)
  }

  const { states, controls, isValid, validateAll } = useForm({
    username: {
      validators: [
        FormValidator.required,
        FormValidator.notEqual(displayName),
        FormValidator.existUsername(buildUsernameExistedFn),
      ],
      value: userName,
      validateOnChange: false,
    },
    message: {
      value: '',
      validateOnChange: true,
    },
    amount: {
      validators: [
        FormValidator.required,
        FormValidator.min(minimumSelectedTokenUsdAmount.toNumber()),
        FormValidator.max(availableSendTipAmount),
      ],
      value: '',
    },
  })

  const parseErrorMessageFallback = useCallback(
    (errorKey: ValidationError, messageContent: React.ReactNode, customFields?: Record<string, string>) => {
      if (customFields?.[errorKey]) return customFields[errorKey]
      return messageContent
    },
    [],
  )

  const formatErrorMessage = (errors: ValidationError[], customFields?: Record<string, string>) => {
    if (errors[0] === ValidationError.Required) {
      return parseErrorMessageFallback(errors[0], <Trans>Please enter a username</Trans>, customFields)
    }
    if (errors[0] === ValidationError.Insufficient)
      return (
        <Trans>
          Insufficient funds. Available balance is {{ amount: getFullDisplayBalance(availableSendTipAmount, 0, 3) }}{' '}
          {{ currency: selectedToken.name }}
        </Trans>
      )
    if (errors[0] === ValidationError.NotEnough)
      return t(' The minimum value is {{minimumAmount}} {{currency}}', {
        minimumAmount: getFullDisplayBalance(minimumSelectedTokenUsdAmount.toFixed(6, BigNumber.ROUND_UP), 0, 6),
        currency: selectedToken.name,
      })
    if (errors[0] === ValidationError.Equal) {
      return <Trans>Cannot tip your self</Trans>
    }
    if (errors[0] === ValidationError.UserNotExist) {
      return <Trans>User not existed</Trans>
    }
  }

  const fetchBalance = async (token: Token) => {
    return Promise.resolve(new BigNumber(tokenBalances[getBalanceTokenKey(token.network, token.code)] || 0))
  }

  const isShowLockBonusAmount = lockAmount.lockBonusAmount.gt(0)
  const isShowTotalDepositRollover = totalDepositRollover.gt(0)

  const inputDisplay = new BigNumber(states.amount.value || 0).decimalPlaces(
    selectedToken.decimals,
    BigNumber.ROUND_FLOOR,
  )

  useEffect(() => {
    return () => {
      if (controls.amount.clear) {
        controls.amount.clear()
      }
    }
  }, [selectedToken])

  const isFiat = useMemo(() => isFiatNetwork(selectedToken?.network), [selectedToken])

  return isDisabledFeaturedOnTelegram ? (
    <WarningOnTelegram />
  ) : (
    <Column gap="var(--pm-space-component, 20px)" height="100%">
      <PaymentFormControl
        state={states.username}
        label={t('Receiver Username')}
        formatErrorMessage={formatErrorMessage}
      >
        <FormInput
          tabIndex={1}
          control={controls.username}
          placeholder={t('Enter receiver username')}
          name="username"
        />
      </PaymentFormControl>

      <Column gap="var(--pm-space-label, 8px)">
        {isMobile ? (
          <>
            <PaymentLabel>
              <Trans>Select Coin</Trans>
            </PaymentLabel>
            <SelectedCurrencyItem
              hideNetwork={false}
              selectedToken={selectedToken}
              onPresentSelectCurrency={() => {
                onPresentSelectTokenModal({
                  selectedToken,
                  onCurrencySelect: setSelectedToken,
                  isIncludeAll: true,
                  hideNetwork: false,
                  hideHUSDToken: true,
                  isAllowCloseAfterSelect: true,
                  fetchBalance,
                  from: PaymentPageEnums.Tip,
                })
              }}
            />
          </>
        ) : (
          <CurrencySelectDropdown
            value={selectedToken}
            onTokenChange={setSelectedToken}
            fetchTokenBalanceFn={fetchBalance}
            isIncludeFiat
            hideNetwork={false}
            options={currencies}
            allCurrencies={currencies}
            from={PaymentPageEnums.Tip}
          />
        )}
      </Column>

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={states.amount}
          label={t('Amount')}
          formatErrorMessage={(error) =>
            formatErrorMessage(error, { [ValidationError.Required]: 'Please enter the amount' })
          }
          disabled={!isAcceptTip}
        >
          <PaymentFormInputAmount
            tabIndex={1}
            control={controls.amount}
            name="amount"
            disabled={!isAcceptTip}
            value={states.amount.value}
            token={selectedToken}
            validators={controls.amount.validators}
            onErrorChanged={controls.amount.onErrorChanged}
            onValueChanged={controls.amount.onValueChanged}
            isInteger={isFiat}
            max={availableSendTipAmount}
            getFee={null}
          />
        </PaymentFormControl>

        <RowBetween>
          <AmountDisplay
            title="Available"
            amount={availableSendTipAmount}
            currency={selectedToken}
            color="textAlt699"
            fontSize="12px"
            fontWeight={400}
          />
          <Text fontSize="12px" color="textAlt699" fontWeight={400} letterSpacing={-0.24}>
            <Trans>No Fee</Trans>
          </Text>
        </RowBetween>
      </Column>

      <Column gap="var(--pm-space-label, 8px)">
        <PaymentFormControl
          state={states.message}
          label={t('Message ({{currentAmount}}/{{total}})', {
            total: MAX_MESSAGE_CHARACTERS,
            currentAmount: states.message.value?.length || 0,
          })}
          formatErrorMessage={formatErrorMessage}
        >
          <InputContainer className="wrap-input-container" pl={2}>
            <InputAdornment
              p="0px 12px 0 4px"
              height="100%"
              endAdornment={
                <RemoveValueInputAdornment
                  onRemove={() => controls.message.onValueChanged('')}
                  value={states.message.value}
                />
              }
            >
              <StyledInput
                tabIndex={1}
                value={states.message.value}
                autoComplete="off"
                onValueChanged={(value, event) => {
                  if (value?.length >= MAX_MESSAGE_CHARACTERS + 1) {
                    event.preventDefault()
                    event.stopPropagation()
                  } else {
                    controls.message.onValueChanged(value)
                  }
                }}
                placeholder={t('Enter message')}
                name="message"
                maxLength={MAX_MESSAGE_CHARACTERS}
              />
            </InputAdornment>
          </InputContainer>
        </PaymentFormControl>

        <RowMiddle gap="8px">
          <StyledSuggestionMessage onClick={() => handleCopySampleMessage('You deserve it!')}>
            <Text>
              <Trans>You deserve it!</Trans>
            </Text>
          </StyledSuggestionMessage>
          <StyledSuggestionMessage onClick={() => handleCopySampleMessage('Thank you!')}>
            <Text>
              <Trans>Thank you!</Trans>
            </Text>
          </StyledSuggestionMessage>
          <StyledSuggestionMessage onClick={() => handleCopySampleMessage('In appreciation')}>
            <Text>
              <Trans>In appreciation</Trans>
            </Text>
          </StyledSuggestionMessage>
        </RowMiddle>
      </Column>

      <ExtraInfo gap="20px" hide={!isShowLockBonusAmount && !isShowTotalDepositRollover}>
        <PaymentStroke />
        <Column gap="8px">
          {isShowLockBonusAmount && (
            <LockBonusAmountWithdrawOverview
              lockAmount={lockAmount.lockBonusAmount}
              onClose={onDismiss}
              selectedToken={selectedToken}
            />
          )}
          {isShowTotalDepositRollover && (
            <DepositRolloverOverview
              openDepositRollovermodal={openDepositRollovermodal}
              selectedToken={selectedToken}
              totalDepositRollover={totalDepositRollover}
            />
          )}
        </Column>
      </ExtraInfo>

      <StickyButtonWrapper>
        <StyledActionButton
          onClick={async () => {
            const isValid = await validateAll()
            if (!isValid) return

            handlePresentTipConfirmModal({
              details: {
                sender: {
                  avatar,
                  displayName,
                },
                receiver: {
                  displayName: states.username.value,
                  ...payloadUserRef.current,
                },
                currency: selectedToken.code,
                network: ChainIdEnum[selectedToken.network],
                amount: inputDisplay?.toString(10),
                message: states.message.value?.trim(),
                createTime: Date.now(),
                id: null,
              },
              onSucceed: () => {
                controls.amount.clear()
              },
            })
          }}
          disabled={!isValid || !states.username.value || !inputDisplay || !isAcceptTip}
        >
          <Trans>Next</Trans>
        </StyledActionButton>
      </StickyButtonWrapper>
      {!isAcceptTip && <TipAcceptModal />}
    </Column>
  )
}

const StyledSuggestionMessage = styled(RowCenter)`
  padding: 4px 8px;
  background: ${({ theme: { colors } }) => colors.backgroundAlt5};
  border-radius: 40px;
  cursor: pointer;
  transition: ${({ theme: { transitions } }) => transitions.fast};

  &:hover {
    transform: scale(1.05);
  }

  ${Text} {
    font-size: 12px;
    font-weight: 400;
    letter-spacing: -0.24;
    line-height: normal;
    color: ${({ theme: { colors } }) => colors.textTertiary};
  }
`

export default TipForm
